'use client';

import { useEffect, useMemo, useState } from 'react';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';

import { config } from '@/config';
import { useChainConfig } from '@/hooks/use-chain-config';
import { useInvestorQuery } from '@/hooks/use-investor-query';
import { useWalletSession } from '@/hooks/use-wallet-session';
import { shouldRetryApiRequest } from '@/lib/api-client';
import { readStorage, removeStorage, writeStorage } from '@/lib/safe-storage';
import {
  fetchInvestorHygiene,
  isHygieneUnsupportedError
} from '../api/get-investor-hygiene';
import { portfolioKeys } from '@/lib/investor-keys';

/** While the backend prepares or recomputes an assessment. */
const CHASE_INTERVAL_MS = 5 * 1000;
/** The standard investor cadence once an assessment exists. */
const SETTLED_INTERVAL_MS = 60 * 1000;
/** The shared error backoff: a failing endpoint is asked far less often. */
const ERROR_INTERVAL_MS = 5 * 60 * 1000;
/**
 * How long the fast chase may run. A first assessment usually lands in
 * under a minute; past about two, asking every five seconds only spends the
 * rate budget other screens need, so the poll drops to the slow cadence and
 * the card says the wait is longer than usual.
 */
export const HYGIENE_CHASE_CAP_MS = 2 * 60 * 1000;

/**
 * How long a wait may go unobserved and still be the same wait. A hook that
 * is mounted refreshes `lastSeenAt` every `WAIT_HEARTBEAT_MS` while the
 * backend says it is preparing, through errors too (the last good payload
 * still says so). A visit that finds the last sighting older than this is a
 * new wait, with its own fast chase; one that finds it fresher carries on
 * the same clock, so leaving Overview and coming straight back does not buy
 * a fresh chase.
 */
const WAIT_GAP_MS = 2 * 60 * 1000;
const WAIT_HEARTBEAT_MS = 30 * 1000;

/**
 * How long a plain 404 (the feature switched off for this deployment) is
 * believed before the endpoint is asked once more. Inside the window
 * nothing asks: not the interval, not window focus, not a return to
 * Overview. Past it, one request, so a tab left open across the deploy that
 * switches `HYGIENE_ENABLED` on picks the canonical score up without a
 * reload.
 */
const HYGIENE_OFF_RECHECK_MS = 15 * 60 * 1000;

interface WaitClock {
  /** When this wait was first seen. The two minute chase runs from here. */
  startedAt: number;
  /** The last time a mounted hook saw the wait still in progress. */
  lastSeenAt: number;
}

/**
 * Two facts this hook has to remember across remounts of Overview and
 * across refetches, kept in the query cache rather than in component state
 * (which restarts on every visit) or in a module variable. Neither sits
 * under `portfolioKeys.all`: a deposit invalidates that whole tree, and
 * neither of these is a request to ask again.
 *
 * - The clock of the current wait, per wallet and chain. Timestamps only.
 * - When the feature was last found switched off, per chain. A plain 404 is
 *   a fact about the deployment, not the wallet.
 */
const waitStartedKey = (address: string | undefined, chainId: number) =>
  ['portfolio-hygiene-wait-started', address?.toLowerCase(), chainId] as const;
const switchedOffKey = (chainId: number) =>
  ['portfolio-hygiene-switched-off', chainId] as const;

/**
 * The switched off sighting is also kept in this browser, so a reload or a
 * new tab inside the window does not ask again: without it every page load
 * of Overview made one failing request. Scoped to the backend this build
 * talks to and the chain, never to the wallet: whether the check is on is
 * a fact about the deployment, so one chain (or one backend) being off
 * never hides another. A timestamp only. Storage that cannot be read or
 * written only means the next load asks, as it did before.
 */
const switchedOffItem = (chainId: number) =>
  `rowa:hygiene-switched-off:${config.backendApiBaseUrl}:${chainId}`;

/** A kept sighting still inside the window, or nothing. */
function readSwitchedOff(chainId: number): number | undefined {
  const at = Number(readStorage(switchedOffItem(chainId)));
  const age = Date.now() - at;
  return Number.isFinite(at) &&
    at > 0 &&
    age >= 0 &&
    age < HYGIENE_OFF_RECHECK_MS
    ? at
    : undefined;
}

/** Held for the life of the page, never fetched, never refetched. */
const REMEMBERED = {
  queryFn: skipToken,
  staleTime: Infinity,
  gcTime: Infinity
} as const;

interface HygienePollState {
  /** React Query's status for the query, `error` after any failed fetch. */
  queryStatus: string;
  error: unknown;
  /** The envelope status of the last good payload, if there is one. */
  hygieneStatus: string | undefined;
  /** The fast chase has already run for `HYGIENE_CHASE_CAP_MS`. */
  chaseExpired: boolean;
}

/**
 * How long to wait before asking again, or `false` to stop asking.
 *
 * A hook that passes its own `refetchInterval` replaces the shared
 * `backOffOnError`, so this owns the error cases itself: the failure is
 * read before the payload, because the last good payload can still say
 * `unavailable` while every poll since has failed.
 */
export function hygienePollInterval({
  queryStatus,
  error,
  hygieneStatus,
  chaseExpired
}: HygienePollState): number | false {
  if (queryStatus === 'error') {
    // A plain 404 means the feature is off for this deployment. That does
    // not change until a deploy, so there is nothing to poll for.
    return isHygieneUnsupportedError(error) ? false : ERROR_INTERVAL_MS;
  }
  if (isWaiting(hygieneStatus)) {
    return chaseExpired ? SETTLED_INTERVAL_MS : CHASE_INTERVAL_MS;
  }
  return SETTLED_INTERVAL_MS;
}

/** The backend is preparing a first assessment or recomputing one. */
function isWaiting(hygieneStatus: string | undefined): boolean {
  return hygieneStatus === 'unavailable' || hygieneStatus === 'refreshing';
}

const capPassed = (startedAt: number | undefined) =>
  startedAt != null && Date.now() - startedAt >= HYGIENE_CHASE_CAP_MS;

/** The clock still describes a wait in progress: it was seen recently. */
const isLive = (clock: WaitClock | undefined): clock is WaitClock =>
  clock != null && Date.now() - clock.lastSeenAt <= WAIT_GAP_MS;

/**
 * Canonical Portfolio Hygiene assessment. Polls at the standard 60s investor
 * cadence once an assessment exists. While the backend is preparing the
 * first assessment (`unavailable`) or recomputing (`refreshing`) it polls at
 * a short interval so the card converges quickly, for at most about two
 * minutes from the moment the wait began. Any failure backs off to five
 * minutes, and does not start those two minutes again.
 *
 * A 404 means `HYGIENE_ENABLED` is off for this deployment — surfaced as
 * `isUnsupported` so the caller can fall back to the documented client-side
 * interim instead of treating it as a failure. For fifteen minutes after it
 * nothing asks again (not the interval, not window focus, not a return to
 * Overview, not a reload or a new tab in this browser); then one request
 * checks whether a deploy switched it on.
 */
export function useInvestorHygiene() {
  const queryClient = useQueryClient();
  const { identityAddress } = useWalletSession();
  const { effectiveChainId } = useChainConfig();
  const waitKey = useMemo(
    () => waitStartedKey(identityAddress, effectiveChainId),
    [identityAddress, effectiveChainId]
  );
  const offKey = useMemo(
    () => switchedOffKey(effectiveChainId),
    [effectiveChainId]
  );

  // When the feature was last found switched off, and whether that is still
  // inside the window. `recheckDue` names the sighting whose window ran out,
  // so a fresh 404 (a new timestamp) is believed again at once. A page load
  // starts from the sighting this browser kept, if it is still fresh.
  const offSince = useQuery<number>({
    queryKey: offKey,
    ...REMEMBERED,
    initialData: () => readSwitchedOff(effectiveChainId)
  }).data;
  const [recheckDue, setRecheckDue] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (offSince == null) return;
    const remaining = offSince + HYGIENE_OFF_RECHECK_MS - Date.now();
    const timer = setTimeout(
      () => setRecheckDue(offSince),
      Math.max(0, remaining)
    );
    return () => clearTimeout(timer);
  }, [offSince]);
  const switchedOff = offSince != null && recheckDue !== offSince;

  const query = useInvestorQuery({
    key: ({ address, chainId }) => portfolioKeys.hygiene(address, chainId),
    fetch: ({ address, chainId }, signal) =>
      fetchInvestorHygiene(address, chainId, signal),
    // Inside the window after a 404 nothing asks: no interval, no focus, no
    // mount. When the window ends this flips back on, and turning a query
    // on fetches it once.
    enabled: !switchedOff,
    // An unsupported backend is a documented state, not a failure to retry.
    retry: (failureCount, error) =>
      isHygieneUnsupportedError(error)
        ? false
        : shouldRetryApiRequest(failureCount, error),
    refetchOnWindowFocus: (activeQuery) =>
      !isHygieneUnsupportedError(activeQuery.state.error),
    refetchInterval: (activeQuery) =>
      hygienePollInterval({
        queryStatus: activeQuery.state.status,
        error: activeQuery.state.error,
        hygieneStatus: activeQuery.state.data?.status,
        // Read when the interval is worked out, so it is always the clock
        // of the wait in progress.
        chaseExpired: capPassed(
          queryClient.getQueryData<WaitClock>(waitKey)?.startedAt
        )
      })
  });

  // Record when the 404 arrived: `errorUpdatedAt` moves with every new
  // 404, so the window restarts each time the deployment says it again.
  const isUnsupportedNow =
    query.isError && isHygieneUnsupportedError(query.error);
  const offAt = isUnsupportedNow ? query.errorUpdatedAt : undefined;
  // An answer from the endpoint means it is on: the 404 is history, and a
  // later visit must not paint the derived score while it loads.
  const answered = query.data !== undefined;
  useEffect(() => {
    if (offAt != null && offAt > 0) {
      queryClient.setQueryData(offKey, offAt);
      writeStorage(switchedOffItem(effectiveChainId), String(offAt));
    } else if (answered) {
      queryClient.removeQueries({ queryKey: offKey, exact: true });
      removeStorage(switchedOffItem(effectiveChainId));
    }
  }, [offAt, answered, queryClient, offKey, effectiveChainId]);

  // The payload's status, whatever the last fetch did: a failed poll keeps
  // the last good payload, so an error does not end the wait or restart it.
  const hygieneStatus = query.data?.status;
  const waiting = isWaiting(hygieneStatus);
  const waitClock = useQuery<WaitClock>({
    queryKey: waitKey,
    ...REMEMBERED
  }).data;
  const waitStartedAt = waitClock?.startedAt;

  useEffect(() => {
    if (!waiting) {
      // An assessment arrived. The next wait is a new wait.
      if (hygieneStatus !== undefined) {
        queryClient.removeQueries({ queryKey: waitKey, exact: true });
      }
      return;
    }
    // Seen waiting: carry on the clock if it was seen recently, otherwise
    // this is a new wait. Repeated while mounted, so a wait watched through
    // an error backoff stays one wait.
    const touch = () => {
      const now = Date.now();
      const current = queryClient.getQueryData<WaitClock>(waitKey);
      queryClient.setQueryData<WaitClock>(
        waitKey,
        isLive(current)
          ? { startedAt: current.startedAt, lastSeenAt: now }
          : { startedAt: now, lastSeenAt: now }
      );
    };
    touch();
    const heartbeat = setInterval(touch, WAIT_HEARTBEAT_MS);
    return () => clearInterval(heartbeat);
  }, [waiting, hygieneStatus, queryClient, waitKey]);

  // Which wait has run past the cap. Seeded from the cache so a return to
  // Overview inside the same wait opens on the long wait state instead of
  // dropping back; a clock that is no longer live is about to be replaced
  // and seeds nothing.
  const [expiredWait, setExpiredWait] = useState<number | undefined>(() =>
    isLive(waitClock) && capPassed(waitClock.startedAt)
      ? waitClock.startedAt
      : undefined
  );
  useEffect(() => {
    if (waitStartedAt == null) return;
    const remaining = waitStartedAt + HYGIENE_CHASE_CAP_MS - Date.now();
    const timer = setTimeout(
      () => setExpiredWait(waitStartedAt),
      Math.max(0, remaining)
    );
    return () => clearTimeout(timer);
  }, [waitStartedAt]);
  const chaseExpired = waitStartedAt != null && expiredWait === waitStartedAt;

  // The recheck after the window is in flight. A query with no data resets
  // to pending while it fetches, which would read as "not switched off" for
  // the length of the request and flash the card to a skeleton every
  // fifteen minutes. Until the backend answers, the last answer stands.
  const awaitingRecheck =
    offSince != null && query.data === undefined && !query.isError;

  return {
    ...query,
    isUnsupported: switchedOff || isUnsupportedNow || awaitingRecheck,
    /**
     * The first assessment is still being prepared after the fast chase ran
     * out. The card says so instead of promising "under a minute" forever.
     */
    isPreparingSlowly: hygieneStatus === 'unavailable' && chaseExpired
  };
}
